/**
 * Device detection utilities for mobile browser experience
 */

import Cookies from "js-cookie";

import { ExperienceFe } from "@intelligems/ig-types/fe-only/experiences";
import { mpMobileDeviceModeSwitched } from "@src/utils/mixpanel";

export { shouldShowMobileExperience } from "@src/utils/shouldShowMobileExperience";

const MOBILE_COOKIE_NAME = "ig-mobile";

/**
 * Detects if the current device is a mobile device based on user agent
 * Checks for common mobile device patterns in the user agent string
 */
export const isMobileDevice = (): boolean => {
  if (typeof window === "undefined" || !window.navigator) {
    return false;
  }

  // https://developer.mozilla.org/en-US/docs/Web/HTTP/Reference/Headers/User-Agent/Firefox#device-specific_user_agent_strings
  return window.navigator.userAgent.toLowerCase().includes("mobi");
};

/**
 * Sets the user's mobile preference
 * Stores in cookie (persists across sessions)
 */
export const setMobilePreference = (isMobile: boolean): void => {
  try {
    Cookies.set(MOBILE_COOKIE_NAME, isMobile ? "true" : "false", { path: "/" });
  } catch (error) {
    console.warn("Unable to set mobile preference:", error);
  }
};

/**
 * Clears the mobile preference from cookies
 */
export const clearMobilePreference = (): void => {
  try {
    Cookies.remove(MOBILE_COOKIE_NAME, { path: "/" });
  } catch (error) {
    console.warn("Unable to clear mobile preference:", error);
  }
};

export const handleSwitchToDesktop = (experience?: ExperienceFe) => {
  mpMobileDeviceModeSwitched("desktop", experience);
  setMobilePreference(false);
  window.location.reload();
};

export const handleSwitchToMobile = (experience?: ExperienceFe) => {
  mpMobileDeviceModeSwitched("mobile", experience);
  setMobilePreference(true);
  window.location.reload();
};

/**
 * Detects if the current device is a Mac computer (macOS)
 * Prefers navigator.userAgentData when available; falls back to navigator.platform for Safari.
 */
export const isMac = (): boolean => {
  if (typeof window === "undefined" || !window.navigator) {
    return false;
  }

  const nav = window.navigator as Navigator & {
    userAgentData?: { platform?: string };
  };

  const platform = nav.userAgentData?.platform;
  if (platform !== undefined) {
    return platform === "macOS";
  }

  // navigator.platform is "MacIntel" etc. on macOS; "iPhone", "iPad", "iPod touch" on iOS
  return /^Mac/.test(nav.platform);
};
